import { G, AU, SOLAR_MASS, secondsToYears, yearsToSeconds } from '../constants';

/**
 * engines/orbital/kepler
 *
 * 二体問題におけるケプラー軌道力学。すべての関数は純粋関数（副作用なし）で、
 * SI単位（メートル・キログラム・秒）を内部の基準単位とする。
 * AU・太陽質量・年単位を使いたい呼び出し側は、engines/constants の
 * 変換ヘルパーで変換してから渡すか、*_AU / *_years サフィックス付きの
 * 便宜関数を使うこと。
 *
 * 参照している式:
 *  - ケプラーの第三法則:      T = 2π √(a³ / GM)
 *  - ヴィスヴィヴァ方程式:    v² = GM(2/r − 1/a)
 *  - 脱出速度:               v_esc = √(2GM/r)
 *  - ケプラー方程式:          M = E − e sin(E)  (Mは平均近点角、Eは離心近点角)
 */

// ============================================================
// ケプラーの第三法則
// ============================================================

/**
 * 軌道長半径 a [m] と中心天体+軌道天体の合計質量 M [kg] から
 * 公転周期 T [s] を求める（ケプラーの第三法則）。
 *
 * T = 2π √(a³ / GM)
 */
export function orbitalPeriod(semiMajorAxisM: number, totalMassKg: number): number {
  if (semiMajorAxisM <= 0) throw new Error('semiMajorAxisM must be positive');
  if (totalMassKg <= 0) throw new Error('totalMassKg must be positive');
  return 2 * Math.PI * Math.sqrt(semiMajorAxisM ** 3 / (G * totalMassKg));
}

/** orbitalPeriod の逆関数。公転周期 T [s] と合計質量 M [kg] から軌道長半径 a [m] を求める。 */
export function semiMajorAxisFromPeriod(periodS: number, totalMassKg: number): number {
  if (periodS <= 0) throw new Error('periodS must be positive');
  if (totalMassKg <= 0) throw new Error('totalMassKg must be positive');
  const a3 = (G * totalMassKg * periodS ** 2) / (4 * Math.PI ** 2);
  return Math.cbrt(a3);
}

/**
 * 便宜関数: AU・太陽質量単位で公転周期を「年」で返す。
 * ダッシュボードUIから直接呼び出すことを想定した薄いラッパー。
 */
export function orbitalPeriodYearsFromAU(semiMajorAxisAU: number, totalMassSolar: number): number {
  const periodS = orbitalPeriod(semiMajorAxisAU * AU, totalMassSolar * SOLAR_MASS);
  return secondsToYears(periodS);
}

// ============================================================
// ヴィスヴィヴァ方程式（軌道速度）
// ============================================================

/**
 * ヴィスヴィヴァ方程式: 中心天体からの距離 r [m] における軌道速度 v [m/s]。
 * 軌道長半径 a [m]、合計質量 M [kg] を与える。
 * 円軌道の場合は r = a を渡せばよい。
 *
 * v² = GM(2/r − 1/a)
 */
export function visViva(distanceM: number, semiMajorAxisM: number, totalMassKg: number): number {
  if (distanceM <= 0) throw new Error('distanceM must be positive');
  if (semiMajorAxisM <= 0) throw new Error('semiMajorAxisM must be positive');
  const v2 = G * totalMassKg * (2 / distanceM - 1 / semiMajorAxisM);
  if (v2 < 0) {
    throw new Error('v^2 is negative — check that distanceM is within the orbit (r <= 2a for bound orbits)');
  }
  return Math.sqrt(v2);
}

/** 円軌道の軌道速度（ヴィスヴィヴァ方程式で a = r とした特殊ケース）。 */
export function circularOrbitalVelocity(radiusM: number, totalMassKg: number): number {
  return Math.sqrt((G * totalMassKg) / radiusM);
}

/**
 * 脱出速度: 距離 r [m] にある物体が中心天体（質量 M [kg]）の重力圏を
 * 脱出するために必要な速度。
 *
 * v_esc = √(2GM/r)
 */
export function escapeVelocity(radiusM: number, centralMassKg: number): number {
  if (radiusM <= 0) throw new Error('radiusM must be positive');
  return Math.sqrt((2 * G * centralMassKg) / radiusM);
}

// ============================================================
// ケプラー方程式（軌道上の位置を時刻から求める）
// ============================================================

/**
 * 平均運動 n [rad/s]: 公転周期 T [s] から角速度を求める。
 * n = 2π / T
 */
export function meanMotion(periodS: number): number {
  return (2 * Math.PI) / periodS;
}

/**
 * ケプラー方程式 M = E − e sin(E) をニュートン・ラフソン法で解き、
 * 離心近点角 E [rad] を返す。
 *
 * @param meanAnomalyRad 平均近点角 M [rad]
 * @param eccentricity   離心率 e (0 <= e < 1、楕円軌道のみ対応)
 * @param tolerance      収束判定の許容誤差（既定 1e-9）
 * @param maxIterations  最大反復回数（既定 50）
 */
export function solveKeplerEquation(
  meanAnomalyRad: number,
  eccentricity: number,
  tolerance = 1e-9,
  maxIterations = 50,
): number {
  if (eccentricity < 0 || eccentricity >= 1) {
    throw new Error('eccentricity must be in [0, 1) for elliptical orbits');
  }

  // 平均近点角を [-π, π] に正規化してから反復を始めると収束が安定する
  const M = ((meanAnomalyRad + Math.PI) % (2 * Math.PI)) - Math.PI;

  let E = eccentricity < 0.8 ? M : Math.PI * Math.sign(M || 1);

  for (let i = 0; i < maxIterations; i++) {
    const f = E - eccentricity * Math.sin(E) - M;
    const fPrime = 1 - eccentricity * Math.cos(E);
    const delta = f / fPrime;
    E -= delta;
    if (Math.abs(delta) < tolerance) break;
  }

  return E;
}

/**
 * 離心近点角 E [rad] と離心率 e から真近点角 ν [rad] を求める。
 * ν = 2 atan2( √(1+e) sin(E/2), √(1-e) cos(E/2) )
 */
export function trueAnomalyFromEccentricAnomaly(eccentricAnomalyRad: number, eccentricity: number): number {
  const E = eccentricAnomalyRad;
  return 2 * Math.atan2(
    Math.sqrt(1 + eccentricity) * Math.sin(E / 2),
    Math.sqrt(1 - eccentricity) * Math.cos(E / 2),
  );
}

export interface OrbitalElements {
  /** 軌道長半径 [m] */
  semiMajorAxisM: number;
  /** 離心率 [0, 1) */
  eccentricity: number;
  /** 合計質量 [kg]（中心天体 + 軌道天体。惑星の場合は恒星質量で近似してよい） */
  totalMassKg: number;
  /** 近点通過時刻 [s]（基準時刻からの経過秒） */
  epochPeriapsisS?: number;
}

export interface OrbitalStateVector {
  /** 軌道面内での位置 x [m]（近点方向を+xとする） */
  x: number;
  /** 軌道面内での位置 y [m] */
  y: number;
  /** 中心天体からの距離 r [m] */
  r: number;
  /** 真近点角 [rad] */
  trueAnomaly: number;
  /** 軌道速度 [m/s]（ヴィスヴィヴァ方程式より） */
  speed: number;
}

/**
 * 時刻 t [s] における軌道上の2次元位置・速度を計算する。
 * 軌道面内座標のみを返す（3D傾斜角・昇交点等は現バージョンでは非対応。
 * MVPでは軌道面を画面平面とみなして描画する）。
 *
 * これが「軌道ビュー」のアニメーションを1フレームずつ駆動する中心関数になる。
 */
export function orbitalStateAtTime(timeS: number, elements: OrbitalElements): OrbitalStateVector {
  const { semiMajorAxisM: a, eccentricity: e, totalMassKg, epochPeriapsisS = 0 } = elements;

  const period = orbitalPeriod(a, totalMassKg);
  const n = meanMotion(period);
  const meanAnomaly = n * (timeS - epochPeriapsisS);

  const E = solveKeplerEquation(meanAnomaly, e);
  const trueAnomaly = trueAnomalyFromEccentricAnomaly(E, e);

  // 軌道面内の距離: r = a(1 - e cos E)
  const r = a * (1 - e * Math.cos(E));

  const x = r * Math.cos(trueAnomaly);
  const y = r * Math.sin(trueAnomaly);

  const speed = visViva(r, a, totalMassKg);

  return { x, y, r, trueAnomaly, speed };
}
